import { useCallback, useEffect, useState } from "react";

export type FormEventHandler = (event: React.FormEvent<EventTarget>) => void;

/**
 * The Hook provides the state of Input field and its computed/non-computed value.
 * It allows to keep different field value and different value in component state.
 * It is useful for example when derived value formatting should happen on some event like onBlur.
 *
 * @param value initial value
 * @param onChange event function fired with computed value
 * @param compute makes value computation or formatting
 */
export const useComputedValue = <T, C extends (value: string) => any>(
  value: T,
  onChange: (value: string) => void,
  compute: C
): [T, FormEventHandler, ReturnType<C>, FormEventHandler] => {
  const [displayedValue, setDisplayedValue] = useState(value);
  const [computedValue, setComputedValue] = useState();

  useEffect(() => {
    setDisplayedValue(value);
  }, [value]);

  /**
   * Keeps a field value not computed
   */
  const onChangeDisplayValue = useCallback((event) => {
    const { value } = event.target;
    setDisplayedValue(value);
  }, []);

  /**
   * Computes a displayed field value
   */
  const onChangeValue = useCallback(
    (event) => {
      const { value } = event.target;
      const computed = compute(value);
      setDisplayedValue(computed);
      setComputedValue(computed);
      onChange(computed);
    },
    [compute, onChange]
  );

  return [displayedValue, onChangeDisplayValue, computedValue, onChangeValue];
};
